/**
 * `GET /devices/{id}/health`, rendered with every `null` kept as a value.
 *
 * Three members of `DeviceHealth` carry `null` as a **statement** rather than as a
 * missing number, and `openapi.yaml` says why for each. This panel renders all three
 * as sentences:
 *
 *   `offline_minutes`      null when online **and** when never reported — "there is
 *                          no silence to measure from, and `last_heartbeat_at: null`
 *                          says so without inventing a number counted from the
 *                          enrolment". Two different sentences, not one blank.
 *   `pending_local_events` null when the device has never reported, because "`0`
 *                          there would be a claim rather than an absence". This is
 *                          the number the revoke confirm step is about to destroy, so
 *                          getting it wrong here is not cosmetic.
 *   `agent_state`          null when the device has never reported.
 *
 * `agent_outdated` is rendered as the **stored verdict** with its reason beside it,
 * because nothing in this build holds a current-version source of truth and a
 * computed answer would be a guess.
 *
 * `flags` renders whatever arrives, including a value this client does not know —
 * `flags` is `readonly string[]` on the wire and dropping an unrecognised entry would
 * be the screen deciding a condition did not happen. The two values `openapi.yaml`
 * documents as **never emitted** are named in a footnote so a reader does not
 * conclude they were forgotten.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type { DeviceHealth } from '../../api/types';
import { DEVICE_HEALTH_FLAGS } from '../../api/types';
import type { MessageKey } from '../../i18n/messages';
import { AgentOffline } from '../../states/AgentOffline';
import { Badge } from '../../states/Badge';
import { offlineLiveness } from './DeviceBadges';

const FLAG_LABEL: Readonly<Record<(typeof DEVICE_HEALTH_FLAGS)[number], MessageKey>> = {
  clock_skew: 'devices.health.flag.clock_skew',
  sequence_gap: 'devices.health.flag.sequence_gap',
  sync_error: 'devices.health.flag.sync_error',
  policy_error: 'devices.health.flag.policy_error',
};

function known(flag: string): flag is (typeof DEVICE_HEALTH_FLAGS)[number] {
  return (DEVICE_HEALTH_FLAGS as readonly string[]).includes(flag);
}

/** A `<dt>/<dd>` pair whose `<dd>` is a sentence when there is no value. */
function Fact({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly testId: string;
}): ReactNode {
  const { t } = useI18n();
  return (
    <>
      <dt>{label}</dt>
      <dd data-testid={testId}>
        {/* `??` and not a ternary: eslint's `prefer-nullish-coalescing` is right that
            they are the same expression, and the sentence is the fallback rather than
            a second branch — an absent value is never rendered as a blank cell. */}
        {value ?? <span className="wp-muted">{t('devices.detail.unknown')}</span>}
      </dd>
    </>
  );
}

export function DeviceHealthPanel({
  health,
  deviceLabel,
  timezone,
}: {
  readonly health: DeviceHealth;
  readonly deviceLabel: string;
  readonly timezone: string;
}): ReactNode {
  const { t, locale } = useI18n();
  const offline = offlineLiveness(health.liveness);

  return (
    <section className="wp-section" aria-labelledby="k5-health-heading" data-testid="k5-health">
      <h3 className="wp-section__title" id="k5-health-heading">
        {t('devices.health.title')}
      </h3>
      <p className="wp-small wp-muted">{t('devices.health.computed')}</p>

      {/* No numeric prop exists on this component, so the screen cannot hand it a 0.
          Rendered only for `stale` and `offline`; a healthy device gets nothing,
          because there is no state to render for a machine that is fine. */}
      {offline === null ? null : (
        <AgentOffline
          liveness={offline}
          lastHeartbeatAt={health.last_heartbeat_at}
          timezone={timezone}
          deviceLabel={deviceLabel}
          testId="k5-health-offline"
        />
      )}

      <p data-testid="k5-health-silence">
        {health.offline_minutes !== null
          ? t('devices.health.offlineMinutes', { minutes: health.offline_minutes })
          : health.last_heartbeat_at === null
            ? t('devices.health.offlineNever')
            : t('devices.health.offlineNone')}
      </p>

      <dl className="wp-deflist">
        <Fact
          label={t('devices.health.agentState')}
          value={health.agent_state}
          testId="k5-health-agent-state"
        />
        <Fact
          label={t('devices.health.policyVersion')}
          value={health.policy_version}
          testId="k5-health-policy-version"
        />
        <Fact
          label={t('devices.health.policyAck')}
          value={
            health.policy_ack_at === null
              ? null
              : formatInstant(health.policy_ack_at, locale, timezone)
          }
          testId="k5-health-policy-ack"
        />
        <Fact
          label={t('devices.health.lastEvent')}
          value={
            health.last_event_at === null
              ? null
              : formatInstant(health.last_event_at, locale, timezone)
          }
          testId="k5-health-last-event"
        />
        <Fact
          label={t('devices.health.sequence')}
          value={health.last_device_sequence === null ? null : String(health.last_device_sequence)}
          testId="k5-health-sequence"
        />
      </dl>

      {health.agent_outdated ? (
        <p data-testid="k5-health-outdated">
          <Badge
            severity="medium"
            icon="circle-alert"
            label={t('devices.health.agentOutdated')}
            title={t('devices.health.agentOutdatedHelp')}
          />
        </p>
      ) : null}

      {/* The queue, as three cases. `null` is never printed as `0`. */}
      <p data-testid="k5-health-queue">
        {health.pending_local_events === null
          ? t('devices.health.queueUnknown')
          : t('devices.health.queue', { events: health.pending_local_events })}
      </p>
      {health.pending_local_events === null ? null : (
        <p className="wp-small wp-muted">{t('devices.health.queueHelp')}</p>
      )}

      <p data-testid="k5-health-rejected">
        {t('devices.health.rejected', { count: health.rejected_events_24h })}
      </p>
      <p className="wp-small wp-muted">{t('devices.health.rejectedHelp')}</p>

      <h4 className="wp-section__title">{t('devices.health.flags')}</h4>
      {health.flags.length === 0 ? (
        <p className="wp-muted" data-testid="k5-health-flags-none">
          {t('devices.health.flagsNone')}
        </p>
      ) : (
        <ul className="wp-flags" data-testid="k5-health-flags">
          {health.flags.map((flag) => (
            <li key={flag}>
              <Badge
                severity="medium"
                icon="circle-alert"
                // An unknown flag renders its wire value rather than disappearing.
                label={known(flag) ? t(FLAG_LABEL[flag]) : flag}
                testId={`k5-health-flag-${flag}`}
              />
            </li>
          ))}
        </ul>
      )}
      <p className="wp-small wp-muted">{t('devices.health.flagsNever')}</p>
    </section>
  );
}
